import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useCategoryTree, buildBreadcrumb } from '@/components/asset-mgr/use-category-tree'
import type { CategoryTreeNode } from '@/types/asset-mgr'

interface CategoryTreeSelectProps {
  value: string | null
  onChange: (next: string | null) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  /** Borderless click-to-edit look (for inline detail rows). Default false = bordered form field. */
  inline?: boolean
}

function nodeMatches(node: CategoryTreeNode, q: string): boolean {
  if (node.name.toLowerCase().includes(q)) return true
  return (node.children ?? []).some((c) => nodeMatches(c, q))
}

/**
 * Single-select category picker rendered as an actual expand/collapse TREE
 * (indented, with chevrons) — same UX as the list-filter CategoryTreeFilter,
 * but selecting a node returns its id and closes. Searching filters the tree
 * and auto-expands matches.
 */
export function CategoryTreeSelect({
  value, onChange, disabled, placeholder, className, inline,
}: CategoryTreeSelectProps) {
  const { t } = useTranslation('assets')
  const resolvedPlaceholder = placeholder ?? t('categorySelect.uncategorized')
  const { tree } = useCategoryTree()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const q = search.trim().toLowerCase()
  const isSearching = q.length > 0

  const selectedCrumb = useMemo(() => {
    if (!value) return null
    const path = buildBreadcrumb(tree, value)
    return path ? path.join(' > ') : null
  }, [tree, value])

  const visible = useMemo(() => {
    if (!isSearching) return tree
    const filter = (nodes: CategoryTreeNode[]): CategoryTreeNode[] =>
      nodes
        .filter((n) => nodeMatches(n, q))
        .map((n) => ({ ...n, children: filter(n.children ?? []) }))
    return filter(tree)
  }, [tree, isSearching, q])

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const select = useCallback((id: string | null) => {
    onChange(id)
    setOpen(false)
    setSearch('')
  }, [onChange])

  if (disabled) {
    return (
      <div className={cn('text-sm text-foreground', className)}>
        {selectedCrumb ?? <span className="text-muted-foreground italic">{resolvedPlaceholder}</span>}
      </div>
    )
  }

  const renderNode = (node: CategoryTreeNode, depth = 0): React.ReactNode => {
    const children = node.children ?? []
    const hasChildren = children.length > 0
    const isExpanded = isSearching || expanded.has(node.id)
    const isSelected = value === node.id
    return (
      <div key={node.id}>
        <div
          className={cn(
            'flex items-center gap-1 rounded px-1.5 py-1 cursor-pointer hover:bg-muted',
            isSelected && 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/40',
          )}
          style={{ paddingLeft: `${depth * 14 + 6}px` }}
          onClick={() => select(node.id)}
        >
          <button
            type="button"
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExpanded(node.id) }}
            tabIndex={-1}
          >
            {hasChildren
              ? (isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)
              : <span className="inline-block h-3 w-3" />}
          </button>
          <span className="flex-1 truncate text-sm">{node.name}</span>
          {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600" />}
        </div>
        {hasChildren && isExpanded && <div>{children.map((c) => renderNode(c, depth + 1))}</div>}
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch('') }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={inline ? 'ghost' : 'outline'}
          role="combobox"
          aria-expanded={open}
          className={cn(
            'font-normal',
            inline
              ? 'group h-8 w-auto max-w-full justify-start gap-1 px-2 -mx-2 hover:bg-accent'
              : 'h-8 w-full justify-between',
            className,
          )}
        >
          <span className={cn('truncate', !selectedCrumb && 'text-muted-foreground')}>
            {selectedCrumb ?? resolvedPlaceholder}
          </span>
          <ChevronsUpDown
            className={cn(
              'ml-1 h-3.5 w-3.5 shrink-0 opacity-50',
              inline && 'opacity-0 transition-opacity group-hover:opacity-100',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('categorySelect.searchPlaceholder')}
            className="h-7 pl-7 text-xs"
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          <div
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-1 cursor-pointer hover:bg-muted',
              value == null && 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/40',
            )}
            style={{ paddingLeft: '6px' }}
            onClick={() => select(null)}
          >
            <span className="inline-block h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-sm text-muted-foreground">{t('categorySelect.uncategorized')}</span>
            {value == null && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600" />}
          </div>
          {visible.length === 0
            ? <div className="px-2 py-1 text-xs text-muted-foreground">{t('categorySelect.noMatches')}</div>
            : visible.map((n) => renderNode(n))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
